import type { ResolvedCollection } from '@rstore/shared';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, onScopeDispose, toValue, watch } from 'vue';
import { useStore } from '#imports';

interface PaginatedQueryPage<TItem> {
	data: TItem[];
	loading?: boolean;
	completed?: boolean;
	error?: unknown;
}

interface PaginatedQueryLike<TItem> {
	pages: { value: ReadonlyArray<PaginatedQueryPage<TItem> | undefined> };
	data: { value: TItem[] | undefined };
	loading: { value: boolean };
	meta: { value: { totalCount?: number } | undefined };
	getPage: (args: any) => PaginatedQueryPage<TItem> | undefined;
	fetchMore: (args: any) => { page?: PaginatedQueryPage<TItem> | undefined } | undefined;
	refresh: () => unknown;
}

interface UsePaginatedQueryOptions<TItem> {
	collectionQuery: PaginatedQueryLike<TItem>;
	pageNumber: MaybeRefOrGetter<number>;
	collectionApiName: MaybeRefOrGetter<string | undefined>;
	enabled?: MaybeRefOrGetter<boolean>;
}

interface MutationPayload {
	collection: ResolvedCollection;
	mutation: 'create' | 'update' | 'delete';
	item?: unknown;
	items?: unknown[];
	key?: unknown;
	keys?: unknown[];
}

interface UsePaginatedQueryReturn<TItem> {
	items: ComputedRef<TItem[]>;
	allItems: ComputedRef<TItem[]>;
	currentPage: ComputedRef<PaginatedQueryPage<TItem> | undefined>;
}

export function usePaginatedQuery<TItem>(
	options: UsePaginatedQueryOptions<TItem>,
): UsePaginatedQueryReturn<TItem> {
	const { collectionQuery, pageNumber, collectionApiName, enabled } = options;
	const store = useStore();

	const currentPageIndex = computed(() => toValue(pageNumber) - 1);

	const currentPage = computed<PaginatedQueryPage<TItem> | undefined>(
		() => collectionQuery.getPage({ pageIndex: currentPageIndex.value }),
	);

	const canFetchMore = computed(() => {
		if (enabled !== undefined && !toValue(enabled)) return false;

		if (currentPageIndex.value === 0) return false;

		const page = currentPage.value;
		if (page?.loading) return false;
		if (page?.completed && !page.error) return false;

		return true;
	});

	// Fetch the current page on demand for pages beyond the first. Page 1
	// (index 0) is owned by rstore's main page. Non-main pages are fetched only
	// when rstore has not completed them yet, or when the previous attempt
	// failed and the user navigates back to that page.
	watch(
		currentPage,
		() => {
			if (!canFetchMore.value) return;

			collectionQuery.fetchMore({ pageIndex: currentPageIndex.value });
		},
		{ immediate: true },
	);

	const items = computed<TItem[]>(() => currentPage.value?.data ?? []);
	const allItems = computed<TItem[]>(() => collectionQuery.data.value ?? []);

	function afterMutation({ collection, mutation, item, items, key, keys }: MutationPayload) {
		const isRelevantCollection = collection.name === toValue(collectionApiName);
		const mutationAffectsCount = mutation === 'create' || mutation === 'delete';

		if (
			!isRelevantCollection
			|| !mutationAffectsCount
			// and query is not already loading (to avoid double refresh in case a create/delete many
			// triggers multiple single mutations)
			|| collectionQuery.loading.value
		) {
			return;
		}

		if (collectionQuery.meta.value?.totalCount !== undefined) {
			let delta = 0;

			if (mutation === 'create') {
				delta = items?.length ?? (item ? 1 : 0);
			}
			else if (mutation === 'delete') {
				delta = -(keys?.length ?? (key ? 1 : 0));
			}

			collectionQuery.meta.value.totalCount += delta;
		}

		collectionQuery.refresh();
	}

	const offAfterMutation = store.$hooks.hook('afterMutation', afterMutation);
	const offAfterManyMutation = store.$hooks.hook('afterManyMutation', afterMutation);

	onScopeDispose(() => {
		offAfterMutation();
		offAfterManyMutation();
	});

	return { items, allItems, currentPage };
}
